import { Fragment } from 'react'
import { clamp01, pct } from './geometry'
import './viz.css'

export interface BulletRow {
  label: string
  min: number
  max: number
  setting: number | null
  live: number | null
  settingDisplay: string
  liveDisplay: string
}

export interface BulletFlag {
  label: string
  on: boolean | null
}

interface BulletBarsProps {
  rows: BulletRow[]
  flags: BulletFlag[]
  ariaLabel: string
}

/**
 * Position (0–100) of `value` on a row's scale, or null when there is nothing
 * honest to draw: no value, a non-finite value, or a scale with no extent
 * (equal, inverted or non-finite bounds).
 */
function place(value: number | null, min: number, max: number): number | null {
  if (value === null || !Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return null
  }
  return clamp01((value - min) / (max - min)) * 100
}

const flagState = (on: boolean | null) => (on === null ? '—' : on ? 'ON' : 'OFF')

/**
 * One knob per row: the configured setting as a marker on a track and the live
 * value as a fill from the start, with both values printed as "live / setting".
 * Each row is two lines (a head line, then the track) so it fits a narrow
 * module. Boolean knobs follow as ON / OFF text, "—" when unknown.
 */
export function BulletBars({ rows, flags, ariaLabel }: BulletBarsProps) {
  return (
    <div className="viz-bullets" role="img" aria-label={ariaLabel}>
      {rows.map((row, i) => {
        const live = place(row.live, row.min, row.max)
        const setting = place(row.setting, row.min, row.max)
        return (
          <div className="viz-bullet-row" key={`${row.label}-${i}`}>
            <div className="viz-bullet-head">
              <span className="viz-bullet-label" title={row.label}>
                {row.label}
              </span>
              <span className="viz-bullet-value">
                {row.liveDisplay} / {row.settingDisplay}
              </span>
            </div>
            <div className="viz-bullet-track">
              {live !== null ? <span className="viz-bullet-live" style={{ width: pct(live) }} /> : null}
              {setting !== null ? <span className="viz-bullet-setting" style={{ left: pct(setting) }} /> : null}
            </div>
          </div>
        )
      })}
      {flags.length > 0 ? (
        <div className="viz-bullet-flags">
          {flags.map((flag, i) => (
            <Fragment key={`${flag.label}-${i}`}>
              {i > 0 ? ' ' : null}
              <span className="viz-bullet-flag">
                {flag.label} <b>{flagState(flag.on)}</b>
              </span>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  )
}
